/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

/** Where a validated response came from, as the service knows it. */
export type ResponseOrigin = {
  method: string;
  /** Request path as passed to the client, ids and query string included. */
  url: string;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ALL_DIGITS = /^\d+$/;
const LONG_HEX = /^[0-9a-f]{16,}$/i;
/** Opaque identifier: long, url-safe, and carries a digit (ids do, route names don't). */
const OPAQUE_ID = /^(?=[\w-]*\d)[\w-]{12,}$/;

const isIdentifier = (segment: string): boolean =>
  UUID.test(segment) ||
  ALL_DIGITS.test(segment) ||
  LONG_HEX.test(segment) ||
  OPAQUE_ID.test(segment);

/**
 * `/um/v2/users/6f1c.../travel-doc` -> `GET /um/v2/users/{id}/travel-doc`.
 *
 * Keeps ids and emails out of logs, and makes the label stable across users so
 * mismatches de-duplicate. The query string is dropped rather than filtered:
 * one account endpoint passes the access token as a query parameter.
 */
export function templatizeEndpoint(origin: ResponseOrigin): string {
  const [pathOnly = ''] = origin.url.split(/[?#]/);

  // Absolute URLs appear for the few non-SSO hosts; keep only the path.
  const path = pathOnly.replace(/^[a-z]+:\/\/[^/]+/i, '');

  const templated = path
    .split('/')
    .map((segment) => {
      if (!segment) {
        return segment;
      }
      if (segment.includes('@')) {
        return '{email}';
      }
      return isIdentifier(segment) ? '{id}' : segment;
    })
    .join('/');

  return `${origin.method.toUpperCase()} ${templated || '/'}`;
}
